import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch, isAdmin } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * What the quality system must hold, against what it actually holds.
 *
 * A document register answers "what do we have". The question an inspection
 * turns on is "what are we missing", and this screen answers that one: every
 * required document, its state, who owns it and by when.
 *
 * A document still in draft is shown apart from one in force. Both are
 * progress; only the second is an answer to an inspector, and collapsing them
 * would make this board read green on the day before an audit.
 */

const STATE = {
  SATISFIED: ['success', 'In force'],
  IN_PROGRESS: ['warning', 'Written, not in force'],
  NOT_APPLICABLE: ['neutral', 'Not applicable'],
  OPEN: ['danger', 'Not started'],
};

const inputStyle = {
  width: '100%', padding: '7px 9px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 12.5,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

export default function QmsReadiness({ notify }) {
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);     // section expanded
  const [filter, setFilter] = useState('ALL');
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, s] = await Promise.all([
        req('/api/qms'),
        req('/api/qms/suggested-links').catch(() => []),
      ]);
      setData(d);
      setSuggestions(Array.isArray(s) ? s : []);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (url, body, ok) => {
    setBusy(true);
    try {
      const r = await req(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      notify?.(ok, 'success');
      await load();
      return r;
    } catch (e) { notify?.(e.message, 'error'); return null; }
    finally { setBusy(false); }
  };

  if (error) return <Banner tone="danger" title="Could not load">{error}</Banner>;
  if (!data) return null;

  const t = data.totals;
  const nothingSeeded = t.required === 0;

  return (
    <div>
      {/* ---- where the company stands ----------------------------------- */}
      <Card style={{ marginBottom: space(2) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Inspection readiness</h3>
            <div style={{ fontSize: 12.5, color: color.textSecondary,
                          marginTop: 4 }}>
              {data.note}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 30, fontWeight: 700,
                          color: data.readiness_percent >= 80 ? color.success
                               : data.readiness_percent >= 40 ? color.warning
                               : color.danger }}>
              {data.readiness_percent}%
            </div>
            <div style={{ fontSize: 11.5, color: color.textSecondary }}>
              documents in force
            </div>
          </div>
        </div>

        {!nothingSeeded && (
          <div style={{ display: 'flex', height: 10, borderRadius: 99,
                        overflow: 'hidden', marginTop: space(1.5),
                        background: color.border }}>
            {[['satisfied', color.success], ['in_progress', color.warning],
              ['open', color.danger]].map(([k, c]) => (
              t[k] > 0 && (
                <div key={k} title={`${k.replace('_', ' ')}: ${t[k]}`}
                     style={{ width: `${(t[k] / data.applicable) * 100}%`,
                              background: c }} />
              )
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap',
                      marginTop: space(1.5), fontSize: 12.5 }}>
          <span><strong>{t.satisfied}</strong> in force</span>
          <span><strong>{t.in_progress}</strong> written, not in force</span>
          <span><strong>{t.open}</strong> not started</span>
          <span style={{ color: color.textSecondary }}>
            <strong>{t.not_applicable}</strong> not applicable
          </span>
          {data.documents_overdue_review > 0 && (
            <span style={{ color: color.danger }}>
              <strong>{data.documents_overdue_review}</strong> overdue review
            </span>
          )}
        </div>

        {nothingSeeded && admin && (
          <div style={{ marginTop: space(1.5) }}>
            <Banner tone="info" title="The required set is not in place yet">
              Load the list of documents a wound-care plant has to be able to
              show an inspector. Nothing is written for you &mdash; this puts
              the checklist in place so the gaps have names.
            </Banner>
            <Btn size="sm" disabled={busy}
                 onClick={() => act('/api/qms/seed', {}, 'Required set loaded.')}>
              Load the required set
            </Btn>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: color.textMuted,
                      marginTop: space(1.5) }}>
          {data.retention_note}
        </div>
      </Card>

      {/* ---- SOPs already written that answer a requirement -------------- */}
      {suggestions.length > 0 && (
        <Card style={{ marginBottom: space(2) }}>
          <h4 style={{ margin: `0 0 4px`, fontSize: 14 }}>
            SOPs you have already written
          </h4>
          <div style={{ fontSize: 12.5, color: color.textSecondary,
                        marginBottom: space(1) }}>
            These look like they answer a requirement. Nothing is linked
            automatically &mdash; a gap closed by a document that nearly fits
            is still a gap.
          </div>
          {suggestions.map((s) => (
            <div key={s.requirement_code}
                 style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 10, padding: '7px 0',
                          borderTop: `1px solid ${color.border}` }}>
              <div style={{ fontSize: 12.5 }}>
                <strong>{s.requirement_code}</strong> {s.requirement_title}
                <div style={{ color: color.textSecondary }}>
                  &rarr; {s.sop_code}: {s.sop_title}
                </div>
              </div>
              {admin && (
                <Btn size="sm" disabled={busy}
                     onClick={() => act(
                       `/api/qms/${s.requirement_code}/link`,
                       { sop_code: s.sop_code },
                       `${s.requirement_code} linked.`)}>
                  Accept
                </Btn>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* ---- filter ------------------------------------------------------ */}
      {!nothingSeeded && (
        <div style={{ display: 'flex', gap: 6, marginBottom: space(1.5),
                      flexWrap: 'wrap' }}>
          {[['ALL', 'Everything'], ['OPEN', 'Not started'],
            ['IN_PROGRESS', 'Written, not in force'],
            ['SATISFIED', 'In force']].map(([v, l]) => (
            <Btn key={v} size="sm"
                 variant={filter === v ? 'primary' : 'secondary'}
                 onClick={() => setFilter(v)}>{l}</Btn>
          ))}
        </div>
      )}

      {/* ---- the sections ------------------------------------------------ */}
      {(data.sections || []).map((sec) => {
        const items = sec.items.filter(
          (i) => filter === 'ALL' || i.state === filter);
        if (!items.length) return null;
        const expanded = open === sec.section;
        return (
          <Card key={sec.section} style={{ marginBottom: space(1.5) }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', cursor: 'pointer' }}
                 onClick={() => setOpen(expanded ? null : sec.section)}>
              <div>
                <strong style={{ fontSize: 14 }}>{sec.label}</strong>
                <div style={{ fontSize: 12, color: color.textSecondary }}>
                  {sec.satisfied} of {sec.required - sec.not_applicable} in force
                  {sec.open > 0 && ` · ${sec.open} not started`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {sec.open > 0 && <Chip tone="danger">{sec.open}</Chip>}
                <span style={{ color: color.textMuted, fontSize: 12 }}>
                  {expanded ? 'hide' : `${items.length} shown`}
                </span>
              </div>
            </div>

            {expanded && (
              <div style={{ marginTop: space(1.5) }}>
                {items.map((i) => {
                  const [tone, label] = STATE[i.state];
                  return (
                    <div key={i.code}
                         style={{ borderTop: `1px solid ${color.border}`,
                                  padding: `${space(1)} 0` }}>
                      <div style={{ display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            <span style={{ color: color.textMuted,
                                           fontWeight: 400 }}>
                              {i.code}
                            </span>{' '}
                            {i.title}
                          </div>
                          {i.why && (
                            <div style={{ fontSize: 11.5,
                                          color: color.textSecondary,
                                          marginTop: 2 }}>
                              {i.why}
                            </div>
                          )}
                          {i.sop_code && (
                            <div style={{ fontSize: 11.5, color: color.success,
                                          marginTop: 3 }}>
                              {i.sop_code}: {i.sop_title}
                            </div>
                          )}
                          {i.doc_number && (
                            <div style={{ fontSize: 11.5, marginTop: 3,
                                          color: color.textSecondary }}>
                              {i.doc_number} v{i.doc_version} &middot; {i.doc_status}
                              {i.review_date && ` · review ${i.review_date}`}
                            </div>
                          )}
                          {i.not_applicable_reason && (
                            <div style={{ fontSize: 11.5, marginTop: 3,
                                          color: color.textMuted }}>
                              Not applicable: {i.not_applicable_reason}
                            </div>
                          )}
                          {(i.owner || i.target_date) && (
                            <div style={{ fontSize: 11.5, marginTop: 3,
                                          color: color.textSecondary }}>
                              {i.owner || 'unassigned'}
                              {i.target_date && ` · due ${i.target_date}`}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Chip tone={tone}>{label}</Chip>
                        </div>
                      </div>

                      {admin && i.state === 'OPEN' && (
                        <OpenActions code={i.code} busy={busy} act={act} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function OpenActions({ code, busy, act }) {
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [showNa, setShowNa] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <div style={{ marginTop: space(1), display: 'grid', gap: 6,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  alignItems: 'end' }}>
      <input style={inputStyle} placeholder="Who will write it"
             value={owner} onChange={(e) => setOwner(e.target.value)} />
      <input style={inputStyle} type="date" value={due}
             onChange={(e) => setDue(e.target.value)} />
      <Btn size="sm" variant="secondary" disabled={busy || (!owner && !due)}
           onClick={() => act(`/api/qms/${code}/assign`,
                              { owner: owner.trim() || null,
                                target_date: due || null },
                              `${code} assigned.`)}>
        Assign
      </Btn>
      {!showNa ? (
        <Btn size="sm" variant="secondary" onClick={() => setShowNa(true)}>
          Not applicable
        </Btn>
      ) : (
        <>
          <input style={inputStyle} placeholder="Why it does not apply here"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
          <Btn size="sm" variant="secondary" disabled={busy}
               onClick={() => act(`/api/qms/${code}/not-applicable`,
                                  { reason: reason.trim() },
                                  `${code} marked not applicable.`)}>
            Save
          </Btn>
        </>
      )}
    </div>
  );
}
